import { Injectable } from "@angular/core";

import { ToastrService } from "ngx-toastr";

import {
  NotificationFailureComponent,
  NotificationSuccessComponent,
} from "@components/shared/notification";
import {
  BackendErrorConstants,
  BackendErrorDynamicMessages,
  CommonConstants,
  TextConstants,
} from "@shared/constants";
import { NotificationError } from "@shared/services";

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private readonly noDuplicateErrors = [TextConstants.SESSION_HAS_EXPIRED];

  constructor(private toastrService: ToastrService) {}

  public showSuccess(message: string): void {
    if (!message) {
      message = TextConstants.DEFAULT_SUCCESS_NOTIFICATION;
    }
    this.toastrService.success(message, undefined, {
      toastComponent: NotificationSuccessComponent,
      timeOut: CommonConstants.NOTIFICATION_DURATION_MS,
    });
  }

  public showError(error: NotificationError | string): void {
    const message = this.getFormattedErrorMessage(error);

    if (this.shouldNotShowErrorMessage(message)) {
      return;
    }

    this.toastrService.error(message, undefined, {
      toastComponent: NotificationFailureComponent,
      timeOut: CommonConstants.NOTIFICATION_ERROR_DURATION_MS,
    });
  }

  private shouldNotShowErrorMessage = (message: string): boolean =>
    this.noDuplicateErrors.includes(message) &&
    !!this.toastrService.findDuplicate(undefined, message, undefined, undefined);

  private getFormattedErrorMessage(error: NotificationError | string): string {
    let message: string;

    if (typeof error === "string") {
      message = error;
    } else {
      switch (error?.status) {
        case 0:
        case 503:
          message = TextConstants.SERVICE_UNAVAILABLE;
          break;
        case 403:
          message = $localize`:@@error.nopermissions:Your user profile has no permissions for this`;
          break;
        default: {
          const errorMessages = error?.error?.errors;

          if (errorMessages?.length) {
            const { code, arguments: errorArgs, message: originalMessage } = errorMessages[0];

            message = originalMessage || message;

            if (BackendErrorDynamicMessages[code]) {
              try {
                const dynamicMessage = BackendErrorDynamicMessages[code](errorArgs);

                if (dynamicMessage) {
                  message = dynamicMessage;
                } else {
                  throw `Error building dynamic message for code: ${code}`;
                }
              } catch (e) {
                console.error(e);
              }
            } else if (BackendErrorConstants[code]) {
              message = BackendErrorConstants[code];
            }
          }
        }
      }
    }

    if (message === "Invalid JWT") {
      message = TextConstants.SESSION_HAS_EXPIRED;
    } else if (!message) {
      message = TextConstants.DEFAULT_ERROR_NOTIFICATION;
    }

    return message;
  }
}
